import { ApolloError } from '@apollo/client';
import {
  deleteAllWithSameProperties,
  useCascade,
  useMutation,
  useTranslation,
} from '@gimlite/common';
import { WizardItemType, Zone } from '@gimlite/ui-v1';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Mutation,
  MutationCreateContractArgs,
} from '../../../../client/graphql';
import {
  ContractMotoristLine,
  statusHelper,
} from '../../../../composition/line/contract-motorist.line';
// import { MotoristLine } from '../../../../composition/line/motorist.line';
import { createContractGQL } from '../../../../common/gql/createContract.gql';
import { convertCredentialsFrontToBack } from '../../../../common/mapper/credential.mapper';
import { ContractWizardInfos } from '../../../../composition/wizard-infos/contract.wizard-infos';
import {
  SummaryForm,
  SummaryFormType,
} from '../../../../form/summary/summary.form';
import { CreateNewSingleContractWizardContext } from '../../createNewSingleContract.wizard';
type ResultPosibility = any;
// | Mutation['createContract']
// | Mutation['createLocalMotorist'];
export const SummaryStep = ({
  context: {
    product,
    contractBase,
    contractDetails,
    credentials,
    addFakeQRCODE,
    contractSearchSelect,
    parkingId,
  },
  validate,
  submit,
}: WizardItemType.Config.Component<CreateNewSingleContractWizardContext>) => {
  const { t, lang } = useTranslation();

  const [created, setCreated] = useState<SummaryFormType.Data.Line[]>();

  const [createContractCall] = useMutation<
    { createContract: Mutation['createContract'] },
    MutationCreateContractArgs
  >(createContractGQL);

  const { isFinish } = useCascade<ResultPosibility>([
    async (prevResult) => {
      try {
        const response = await createContractCall({
          variables: {
            input: {
              parkingId: parkingId!,
              productId: product.id!,
              startDate: contractBase.startDate!,
              stopDate: contractBase.endDate!,
              address: {
                address1: contractDetails.address1,
                address2: contractDetails.address2,
                city: contractDetails.city,
                country: contractDetails.country,
                zipcode: contractDetails.zipcode,
              },
              description: contractBase.description,
              reference: contractBase.reference,
              motoristId: contractDetails?.motoristId,
              shortUid: contractDetails?.shortUid,
              antiPassBackMode: contractBase.APBMode,
              isEnabled: contractBase.state,
              name1: contractDetails.name1,
              name2: contractDetails.name2,
              lang: contractDetails.lang,
              username: contractDetails.username,
              articleCode: contractBase.articleCode!,
              sendInvitation: contractDetails.inviteMotorist,
              freeFlagIsEnabled:
                contractBase.APBMode !== 'NONE' ? !contractBase.APBNext : null,
              credentials: convertCredentialsFrontToBack(
                credentials.desactived.reduce(
                  (acc, credential) =>
                    deleteAllWithSameProperties(acc, credential),
                  contractSearchSelect
                    ? credentials.actived
                    : addFakeQRCODE
                      ? credentials.actived.filter(
                          ({ name }) => name !== 'QRCODE',
                        )
                      : credentials.actived,
                ),
              ).map(({ _id, ...rest }) => ({
                ...rest,
              })),
            },
          },
        });

        if (
          response?.errors &&
          Array.isArray(response?.errors) &&
          response?.errors.length > 0
        ) {
          throw response?.errors[0]?.message;
        }

        const createContract = response?.data?.createContract;

        setCreated((list) => [
          ...(list ? list : []),
          {
            _id: uuidv4(),
            items: {
              component: (
                <ContractMotoristLine
                  data={{
                    firstName: createContract?.name1 || null,
                    lastName: createContract?.name2 || null,
                    contractId: createContract?.ospContractId || null,
                    status: statusHelper({
                      isEnabled: createContract?.isEnabled!,
                      startDate: new Date(createContract?.startDate!),
                      stopDate: new Date(createContract?.stopDate!),
                    }),
                  }}
                  config={{ action: false }}
                />
              ),
              state: {
                type: 'success',
              },
            },
          },
        ]);

        return { title: 'CreateContract', response: createContract };
      } catch (error: unknown) {
        setCreated((list) => [
          ...(list ? list : []),
          {
            _id: uuidv4(),
            items: {
              component: (
                <ContractMotoristLine
                  data={{
                    firstName: contractDetails.name1,
                    lastName: contractDetails.name2,
                    contractId: null,
                    status: statusHelper({
                      isEnabled: contractBase.state,
                      startDate: new Date(contractBase.startDate!),
                      stopDate: new Date(contractBase.endDate!),
                    }),
                  }}
                  config={{ action: false }}
                />
              ),
              state: {
                type: 'error',
                message:
                  error instanceof ApolloError
                    ? `${error.message}`
                    : `${error}`,
              },
            },
          },
        ]);

        return {
          title: 'CreateContract',
          response: new Error(
            error instanceof ApolloError ? `${error.message}` : `${error}`,
          ),
        };
      }
    },
  ]);

  useEffect(() => {
    validate(isFinish);
  }, [isFinish]);

  return (
    <Zone
      config={{
        gap: {
          y: 1,
          x: 1,
        },
        zones: [['info', 'list']],
        rows: ['1fr'],
        columns: ['min-content', '1fr'],
      }}
    >
      <Zone.Area config={{ area: 'info' }}>
        <ContractWizardInfos
          data={{
            productName: product.id ? t(`product-${product.id}-name`) : null,
            APBMode: contractBase.APBMode,
            APBNext:
              contractBase.APBMode !== 'NONE' ? contractBase.APBNext : null,
            state: contractBase.state,
            startDate: contractBase.startDate,
            endDate: contractBase.endDate,
            description: contractBase.description,
            reference: contractBase.reference,
            articleCode: contractBase.articleCode,
          }}
        />
      </Zone.Area>

      <Zone.Area config={{ area: 'list' }}>
        <SummaryForm
          data={{
            list: created,
          }}
        />
      </Zone.Area>
    </Zone>
  );
};
